import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { UseCases } from "@planzone/core";
import {
  useAddResourceToResourceGroup,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";

import { useModal } from "@root/client/modal";
import { Safeguard } from "@scenes/safeguard";
import { ContextualMenu } from "@common/contextualMenu";
import { DropBox, useDragAndDrop } from "@common/dragAndDrop";
import { Props } from "./types";
import { GroupIcon } from "./groupIcon";
import styles from "./styles.scss";

function Group({ group, isSelected, select, unSelect }: Props) {
  const { pushView } = useModal();
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { data } = useDragAndDrop();
  const { isRequestPending, addResourceToResourceGroup } =
    useAddResourceToResourceGroup(group, data.payload);

  return (
    <DropBox
      acceptedTypes={["MOVE_RESOURCE"]}
      drop={({ payload }) =>
        !isRequestPending &&
        addResourceToResourceGroup({ group, resource: payload })
      }
      isDisabled={({ payload }) =>
        group.resources.find(({ uuid }) => uuid === payload.uuid)
      }
    >
      {(_, canDrop) => (
        <div
          className={classNames(styles.group, { [styles.droppable]: canDrop })}
        >
          <div
            className={classNames(styles.main, {
              [styles.selected]: isSelected,
            })}
            onClick={() => (isSelected ? unSelect(group) : select(group))}
          >
            <GroupIcon className={styles.groupIcon} />
            <p className={styles.name}>{group.name}</p>
          </div>
          <Safeguard
            useCases={[
              UseCases.editResourceGroup,
              UseCases.removeResourceGroup,
            ]}
          >
            {({ canEditResourceGroup, canRemoveResourceGroup }) =>
              (canEditResourceGroup || canRemoveResourceGroup) && (
                <div className={styles.menu}>
                  <ContextualMenu
                    items={[
                      ...(canEditResourceGroup
                        ? [
                            {
                              name: t("membersList.contextualMenu.edit"),
                              act: () =>
                                pushView({
                                  key: UseCases.editResourceGroup,
                                  data: { group, namespace: planzone.uuid },
                                }),
                            },
                          ]
                        : []),
                      ...(canRemoveResourceGroup
                        ? [
                            {
                              name: t("membersList.contextualMenu.delete"),
                              color: "#DB5F63FF",
                              act: () =>
                                pushView({
                                  key: UseCases.removeResourceGroup,
                                  data: group,
                                }),
                            },
                          ]
                        : []),
                    ]}
                  />
                </div>
              )
            }
          </Safeguard>
        </div>
      )}
    </DropBox>
  );
}

export { Group };
